import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
    toJS,
} from "mobx";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { Grid, styled, Tooltip } from "@mui/material";
import {
    GridPaginationModel,
    GridRenderCellParams,
    GridRowParams,
} from "@mui/x-data-grid-pro";
import theme from "Theme/AppTheme";
import AcxChip from "components/UI/AcxChip";
import { durationColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DurationColType";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import { EvalType } from "models/Evaluation";
import type { SkillGrouping } from "../_DataEndpointConfig/Types";
import moment from "moment";
import { Link } from "react-router-dom";
import { Routes } from "components/Navigation/Routes";
import { AuthStore } from "stores/AuthStore";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
    type LanguageFilterObject,
    type ModuleScore,
    type PrimaryDriver,
    type PrimaryTopic,
    type QuestionResultFilter,
    type SimpleTheme,
    type SimpleTopicCluster,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import {
    getQuestionResultLabelParts,
    loadQuestionResultLabelData,
} from "components/ApplicationFilters/FiltersAndChips/QuestionResult/questionResultLabels";
import {
    isOpenEndedCallDuration,
    MAX_CALL_DURATION,
} from "components/ApplicationFilters/FiltersAndChips/CallDuration/callDurationSliderState";
import { MAX_MESSAGE_COUNT } from "components/ApplicationFilters/FiltersAndChips/MessageCount/messageCountSliderState";
import { MODULE_SCORE_MAX } from "components/ApplicationFilters/FiltersAndChips/ModuleScores/moduleScoreLabels";
import { BaseStore } from "stores/BaseStore";
import {
    ApplicationFilterItemsStore,
    type FilterKey,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import ContextStore from "stores/ContextStore";
import ConversationsStore, {
    Topics,
} from "components/Conversations/Stores/ConversationsStore";
import { SignalsService } from "services/SignalsService";
import { XValueType } from "utils/ReportDataToGoogleFormat";
import {
    parseFromISO,
    serializeToUtc,
    standardDateFormat,
    toIsoByDateReference,
} from "utils/DateTimeUtils";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { EvaluationType } from "stores/ApplicationFilters/Filters/EvaluationType";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import { MediaType } from "stores/ApplicationFilters/Filters/MediaType";
import { SafetyEventAsString } from "stores/ApplicationFilters/Filters/SafetyEvent";
import { Sentiment } from "stores/ApplicationFilters/Filters/Sentiment";
import { SourceFilesStepStore } from "components/Admin/AdminUIComponents/AudioFileSamplerV2/Stores/SourceFilesStepStore";
import { AudioFileSamplerStore } from "components/Admin/AdminUIComponents/AudioFileSampler/Stores/AudioFileSamplerStore";
import { RecommendationStepStore } from "components/Admin/AdminUIComponents/AudioFileSamplerV2/Stores/RecommendationStepStore";
import {
    IClassifiersFromDrilldown,
    ISignalsDrilldownResponse,
    ISignalsDriversDrilldownResponse,
    ISignalsTopicsDrilldownResponse,
    SignalDataEndpoint,
    SignalsEndpoints,
    SignalsRequest,
    SignalsTemporalLabels,
    type NumericRangeGroupByOption,
} from "components/Signals/SignalsModels";
import { ML_Topic_LLM } from "components/Conversations/Views/Drawer/components/ConversationClassifierChipList/Helpers";
import AcxFormatters from "components/UI/AcxDataGrid/Formatters/AcxFormatters";
import Markdown from "markdown-to-jsx";
import { dateColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import AcxProgress from "components/UI/AcxDataGrid/Formatters/AcxProgress";
import AcxClickableCell from "components/UI/AcxDataGrid/AcxClickableCell";
import { formatSegmentHeading } from "components/UI/AcxTranscription/utils";
import { getXDateRangeLabel } from "../ChartCard/dateBucketLabels";
import { v4 as uuidv4 } from "uuid";
import AgentStore from "components/KBo/Stores/AgentStore";
import type { DataPointQueryContext } from "services/AcxAgentService";
import OrgOptionsStore from "stores/OrgOptionsStore";
import diffObjects from "utils/ObjectDiff";
import _ from "lodash";
import type {
    DrilldownBehavior,
    DrilldownSelection,
    DrilldownSource,
    SelectedDataPoint,
    SelectedDriver,
    SelectedTopic,
} from "./DrilldownTypes";
import { temporalGrain } from "../DataAggregation";
import { Operator } from "models/Rule";
import {
    findMissingCapabilities,
    hasTemporalGrain,
    normalizeEndpointForDrilldown,
    resolveAggregationDrilldownTitle,
    resolveViewDataClassification,
} from "./DrilldownHelpers";
import {
    applyGroupingSelectionToFilters,
    applyGroupingSelectionToRequestState,
    clearGroupingPayloadFromSignalsRequest,
    getDateBucketSpan,
    getDateGroupingSelection,
    getNumericRangeBucketBounds,
    getNumericRangeGroupingSelection,
    normalizeSelectionForDrilldownStrategy,
    resolveDateGroupingTemporalGrain,
} from "./groupingDrilldown";
import { isFilterValueEmpty } from "../ObjectFilters";
import { getEndpointBehaviorConfig } from "../_DataEndpointConfig";

/**
 * Filters whose KBo prompt title reads better in the singular when only one value is selected.
 * Keyed by filter key; a key absent here always keeps its plural title.
 */
const SINGULAR_FILTER_TITLE_BY_KEY: Record<string, string | undefined> = {
    agentIds: "Agent",
    agentManagerIds: "Agent Manager",
};

@AcxStore
class DrilldownStore extends BaseStore {
    private static readonly MAX_FILTER_VALUES_IN_SUMMARY = 5;

    readonly authStore: AuthStore;
    readonly applicationFiltersStore: ApplicationFiltersStore;
    readonly applicationFilterValuesStore: ApplicationFilterValuesStore;
    readonly applicationFilterItemsStore: ApplicationFilterItemsStore;
    readonly contextStore: ContextStore;
    readonly conversationsStore: ConversationsStore;
    readonly agentStore: AgentStore;
    readonly optionsStore: OrgOptionsStore;
    sourceFilesStepStore: SourceFilesStepStore;
    audioFileSamplerStore: AudioFileSamplerStore;
    recommendationStepStore: RecommendationStepStore;

    @observable
    selectedDataDgStore: AcxDataGridStore = new AcxDataGridStore(
        "SignalsDrilldown",
        "Signals",
    );

    readonly signalsService: SignalsService;

    @observable
    selectedDataPoint: SelectedDataPoint | null = null;

    @observable
    selectedTopic: SelectedTopic | null = null;

    @observable
    selectedDriver: SelectedDriver | null = null;

    @observable
    drilldownDialogTitle: string = "";

    @observable
    drilldownSubtitle: string = "";

    @observable
    showSelectedDataDialog: boolean = false;

    private abortController: AbortController = new AbortController();

    @observable
    drilldownSql: string | undefined;

    @observable
    drilldownState: SignalsRequest<Record<string, unknown>> | null = null;

    source: DrilldownSource;
    strategy: DrilldownBehavior | undefined;

    private lastDrilldownSource: DrilldownSource | null = null;

    public constructor(rootStore: IRootStore, source: DrilldownSource) {
        super("Drilldown Store");
        makeObservable(this);

        // Setup service and stores
        this.signalsService = new SignalsService();
        this.authStore = rootStore.getStore(AuthStore);
        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore,
        );
        this.applicationFilterValuesStore = rootStore.getStore(
            ApplicationFilterValuesStore,
        );
        this.applicationFilterItemsStore = rootStore.getStore(
            ApplicationFilterItemsStore,
        );
        this.contextStore = rootStore.getStore(ContextStore);
        this.conversationsStore = rootStore.getStore(ConversationsStore);
        this.sourceFilesStepStore = rootStore.getStore(SourceFilesStepStore);
        this.audioFileSamplerStore = rootStore.getStore(AudioFileSamplerStore);
        this.recommendationStepStore = rootStore.getStore(
            RecommendationStepStore,
        );
        this.agentStore = rootStore.getStore(AgentStore);
        this.optionsStore = rootStore.getStore(OrgOptionsStore);

        this.initializeSource(source);

        this.dataGridSetup();

        reaction(
            () => this.showSelectedDataDialog,
            () => {
                this.initializeSampler();
            },
        );
    }

    private initializeSource(source: DrilldownSource) {
        if (!source.dataEndpoint) {
            throw new Error("Drilldown source must provide a dataEndpoint");
        }

        if (!source.state) {
            throw new Error("Drilldown source must provide state");
        }

        const strategy = this.getStrategy(source);
        const missingCapabilities = findMissingCapabilities(strategy, source);
        if (missingCapabilities.length > 0) {
            throw new Error(
                `Drilldown source is missing required capabilities: ${missingCapabilities.join(
                    ", ",
                )}`,
            );
        }

        this.source = source;
        this.strategy = strategy;
        this.lastDrilldownSource = source;
        this.drilldownState = null;
    }

    private getStrategy(
        source?: DrilldownSource,
    ): DrilldownBehavior | undefined {
        if (this.strategy && !source) return this.strategy;
        if (!source?.dataEndpoint) return source?.config?.drilldown;
        return source.config?.drilldown;
    }

    private syncDrilldownStateFromSource(
        source: DrilldownSource | null = this.source,
    ) {
        if (!source?.state) {
            this.drilldownState = null;
            return;
        }

        // Always carry the latest merged (dashboard + object) filters into drilldown state. The
        // source holds only its own request fields; these filters are what complete them into a
        // whole request, which is why the assertion below is sound and the source's own type is not.
        const drilldownState = {
            ...toJS(source.state),
            ...this.getBaseFiltersForDrilldown(source),
        } as SignalsRequest<Record<string, unknown>>;

        clearGroupingPayloadFromSignalsRequest(drilldownState);
        this.drilldownState = drilldownState;
    }

    private getDrilldownState(): SignalsRequest<
        Record<string, unknown>
    > | null {
        if (this.drilldownState) {
            return this.drilldownState;
        }

        this.syncDrilldownStateFromSource();

        return this.drilldownState;
    }

    /* General */

    initializeSampler = () => {
        this.sourceFilesStepStore.isFromOutsideSampler = true;
        this.sourceFilesStepStore.orgSelectStore.selectOrg(
            this.authStore.orgStore.selectedOrganization,
        );
        this.audioFileSamplerStore.isFromOutsideSampler = true;
        this.audioFileSamplerStore.orgId =
            this.authStore.orgStore.selectedOrganization?.id;
    };

    @action
    dataGridSetup = () => {
        this.selectedDataDgStore.removeHeight = "75px";
        this.selectedDataDgStore.hideVertIcon = true;
        this.selectedDataDgStore.hideHeader = true;

        this.selectedDataDgStore.paginationMode = "server";
        this.selectedDataDgStore.filterMode = "server";
        this.selectedDataDgStore.sortingMode = "server";
        this.selectedDataDgStore.hideFilter = true;

        this.selectedDataDgStore.pagination = true;
        this.selectedDataDgStore.pageSizeOptions = [10];
        this.selectedDataDgStore.paginationSize = 10;
        this.selectedDataDgStore.disableLocalConfig = true;

        this.selectedDataDgStore.isRowSelectable = (param: GridRowParams) => {
            const isRowReadyToSample = param.row.isReadyToSample;
            const selectedRowCount =
                this.selectedDataDgStore.selectedRows.length;
            const isSelected = this.selectedDataDgStore.selectedRowIds.includes(
                param.row.id,
            );
            return isRowReadyToSample && (selectedRowCount < 10 || isSelected);
        };
    };

    @action
    closeDrilldownDialog = () => {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.lastDrilldownSource = null;
        this.drilldownState = null;

        this.selectedDataDgStore.paginationModel!.page = 0;
        this.selectedDataDgStore.clearSelected();
        this.selectedDataDgStore.rows = [];
        this.selectedDataDgStore.rowCount = undefined;

        this.showSelectedDataDialog = false;
    };

    /* Selections */

    @action
    selectDataPoint({
        series,
        x,
        y,
        seriesIdentifier,
        count,
    }: {
        series: string | null;
        x: XValueType | null;
        y: any;
        seriesIdentifier?: string | null;
        count: number | null;
    }) {
        let formattedValue = x?.f as any;

        const xValue =
            formattedValue instanceof Date
                ? moment(formattedValue).format("MMM D, YYYY")
                : formattedValue;

        this.syncDrilldownStateFromSource();

        this.selectedDataPoint = {
            x: xValue,
            y,
            series,
            seriesIdentifier: seriesIdentifier ?? undefined,
            count,
            xLabel: x?.v?.toString(),
        };
    }

    /**
     * TreeMap selection bypasses Google's XValueType wrapping (the TreeMap
     * primitive doesn't fire a `select` event in the same shape as bar / line
     * / donut). Captures the clicked cell's id, label, raw metadata, and
     * volume so the point menu can read `hasChildren` and the zoom-in action
     * can snapshot the row's filter-relevant fields.
     */
    @action
    selectTreeMapNode({
        xIdentifier,
        xLabel,
        value,
        metadata,
    }: {
        xIdentifier: string | undefined;
        xLabel: string;
        value: number;
        metadata?: { name: string; value: unknown }[];
    }) {
        this.syncDrilldownStateFromSource();
        this.selectedDataPoint = {
            x: xLabel,
            xLabel,
            xIdentifier,
            y: value,
            count: value,
            series: null,
            metadata,
        };
    }

    /**
     * Insight (small) card selection. The whole card is the selection — there is no plotted point
     * to resolve an x or a series from, and the stat the card renders (a duration, a percent) is
     * not a conversation count, so the count comes from the card's payload instead of its y value.
     * Exists for the same reason as {@link selectTreeMapNode}: the shape a chart `select` event
     * produces does not describe this surface.
     */
    @action
    selectSmallCard({
        title,
        count,
    }: {
        title?: string;
        count?: number | null;
    }) {
        this.syncDrilldownStateFromSource();

        this.selectedDataPoint = {
            x: title ?? null,
            xLabel: title,
            y: undefined,
            series: null,
            count: count ?? null,
        };
    }

    @action
    selectTopic({
        selectedTopic,
        topicNumber,
        themeNumber,
        x,
        series,
        count,
    }: {
        selectedTopic: string;
        topicNumber: number;
        themeNumber?: number;
        x?: string | number | null;
        series?: string | null;
        count?: number | null;
    }) {
        this.syncDrilldownStateFromSource();

        this.selectedTopic = {
            selectedTopic,
            topicNumber,
            themeNumber,
            x,
            series,
            count,
        };
    }

    @action
    selectDriver({
        selectedDriver,
        driverIndex,
        themeNumber,
        themeId,
        x,
        series,
        count,
    }: {
        selectedDriver: string;
        driverIndex: number;
        themeNumber?: number;
        themeId?: string;
        x?: string | number | null;
        series?: string | null;
        count?: number | null;
    }) {
        this.syncDrilldownStateFromSource();

        this.selectedDriver = {
            selectedDriver,
            driverIndex,
            themeNumber,
            themeId,
            x,
            series,
            count,
        };
    }

    @action
    clearSelection() {
        this.selectedDataPoint = null;
        this.selectedTopic = null;
        this.selectedDriver = null;
        this.drilldownState = null;
    }

    /* Actions */

    // Primary drilldown action
    viewData() {
        const source = this.source;
        if (!source) return;
        if (this.selectedTopic) {
            this.viewDataTopic();
        } else if (this.selectedDriver) {
            this.viewDataDriver();
        } else if (this.selectedDataPoint && source) {
            this.viewDataPoint();
        }
    }

    async viewDataTopic() {
        if (!this.selectedTopic) return;
        const source = this.source;
        if (source) {
            this.lastDrilldownSource = source;
        }
        this.selectedDataDgStore.setLoading(true);
        this.drilldownDialogTitle = this.getDrilldownTitle({ source });
        this.drilldownSubtitle = this.getDrilldownSubtitle({ source });

        const selectedTopicColumns =
            this.getConversationColumnsWithoutClassification();

        this.showSelectedDataDialog = true;
        this.selectedDataDgStore.clearSelected();
        this.selectedDataDgStore.rows = [];

        return await this.setupAsyncTask(
            "Load Signals Drill Down",
            async () => {
                if (this.selectedTopic) {
                    const requestReady = this.requestStateSetup({
                        x: this.selectedTopic.x,
                        series: this.selectedTopic.series,
                        seriesIdentifier:
                            this.selectedDataPoint?.seriesIdentifier,
                        xLabel: this.selectedDataPoint?.xLabel,
                    });

                    if (!requestReady) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    const drilldownState = this.getDrilldownState();
                    if (!drilldownState) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    this.selectedDataDgStore.checkboxSelection = true;
                    this.selectedDataDgStore.setColumns(selectedTopicColumns);
                    drilldownState.topicNumber = this.selectedTopic.topicNumber;
                    drilldownState.themeNumber = this.selectedTopic.themeNumber;
                    drilldownState.themeId = this.selectedTopic.themeId;

                    const res =
                        await this.signalsService.getConversationsForTopic(
                            drilldownState,
                        );
                    runInAction(() => {
                        this.handleDrilldownResponse({
                            res,
                        });
                    });
                }
            },
        );
    }

    /**
     * Breaks the selected Theme point into the Primary Drivers that resolved to it. The Topic
     * counterpart is {@link viewTopics}; these Cards read the Primary Driver's Theme, so Drivers
     * are the breakdown that can actually describe their population.
     */
    async viewDrivers() {
        const source = this.source;
        if (!source) return;
        this.selectedDataDgStore.setLoading(true);
        this.drilldownDialogTitle = this.getDrilldownTitle({
            source,
            isTopicsView: true,
        });
        this.drilldownSubtitle = this.getDrilldownSubtitle({
            source,
            isTopicsView: true,
        });
        this.lastDrilldownSource = source;

        this.showSelectedDataDialog = true;
        this.selectedDataDgStore.clearSelected();
        this.selectedDataDgStore.rows = [];

        return await this.setupAsyncTask(
            "Load Signals Drill Down",
            async () => {
                if (this.selectedDataPoint) {
                    const requestReady = this.requestStateSetup({
                        x: this.selectedDataPoint.x,
                        series: this.selectedDataPoint.series,
                        seriesIdentifier:
                            this.selectedDataPoint.seriesIdentifier,
                        xIdentifier: this.selectedDataPoint.xIdentifier,
                        xLabel: this.selectedDataPoint.xLabel,
                        metadata: this.selectedDataPoint.metadata,
                    });
                    if (!requestReady) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    const drilldownState = this.getDrilldownState();
                    if (!drilldownState) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    this.selectedDataDgStore.checkboxSelection = false;
                    this.selectedDataDgStore.setColumns(this.driversColumns);
                    this.selectedDataDgStore.onCellClick = (params) => {
                        if (params.field === "name") {
                            const volume =
                                params.row.volume ?? params.row.count;
                            const driverCount =
                                volume !== undefined &&
                                volume !== null &&
                                !Number.isNaN(Number(volume))
                                    ? Number(volume)
                                    : null;

                            this.selectDriver({
                                selectedDriver: params.value as string,
                                driverIndex: params.row.driverIndex,
                                themeId: drilldownState.themeId as
                                    | string
                                    | undefined,
                                x: this.selectedDataPoint!.x,
                                series: this.selectedDataPoint!.series,
                                count: driverCount,
                            });
                            this.viewDataDriver();
                        }
                    };

                    const drilldownEndpoint =
                        this.getDriversDrilldownEndpoint(source);
                    if (!drilldownEndpoint) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    const res =
                        await this.signalsService.getDriverDrillDownForChart(
                            drilldownEndpoint,
                            drilldownState,
                            this.abortController.signal,
                        );

                    runInAction(() => {
                        this.handleDriverDrilldownResponse({ res });
                    });
                }
            },
        );
    }

    /** The Conversations behind one Driver inside the selected Theme. */
    async viewDataDriver() {
        if (!this.selectedDriver) return;
        const source = this.source;
        if (source) {
            this.lastDrilldownSource = source;
        }
        this.selectedDataDgStore.setLoading(true);
        this.drilldownDialogTitle = this.getDrilldownTitle({ source });
        this.drilldownSubtitle = this.getDrilldownSubtitle({ source });

        // The selected Driver already scopes every row, so the classification column would only
        // repeat the same value. This mirrors the selected Topic flow.
        const selectedDriverColumns =
            this.getConversationColumnsWithoutClassification();

        this.showSelectedDataDialog = true;
        this.selectedDataDgStore.clearSelected();
        this.selectedDataDgStore.rows = [];

        return await this.setupAsyncTask(
            "Load Signals Drill Down",
            async () => {
                if (this.selectedDriver) {
                    const requestReady = this.requestStateSetup({
                        x: this.selectedDriver.x,
                        series: this.selectedDriver.series,
                        seriesIdentifier:
                            this.selectedDataPoint?.seriesIdentifier,
                        xLabel: this.selectedDataPoint?.xLabel,
                    });

                    if (!requestReady) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    const drilldownState = this.getDrilldownState();
                    if (!drilldownState) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    this.selectedDataDgStore.checkboxSelection = true;
                    this.selectedDataDgStore.setColumns(selectedDriverColumns);
                    drilldownState.driverIndex = this.selectedDriver.driverIndex;
                    drilldownState.themeNumber = this.selectedDriver.themeNumber;
                    drilldownState.themeId = this.selectedDriver.themeId;

                    const res =
                        await this.signalsService.getConversationsForDriver(
                            drilldownState,
                        );
                    runInAction(() => {
                        this.handleDrilldownResponse({
                            res,
                        });
                    });
                }
            },
        );
    }

    async viewDataPoint() {
        const source = this.source;
        if (!source) return;
        this.selectedDataDgStore.setLoading(true);
        this.drilldownDialogTitle = this.getDrilldownTitle({ source });
        this.drilldownSubtitle = this.getDrilldownSubtitle({ source });
        this.lastDrilldownSource = source;

        this.showSelectedDataDialog = true;
        this.selectedDataDgStore.clearSelected();
        this.selectedDataDgStore.rows = [];

        return await this.setupAsyncTask(
            "Load Signals Drill Down",
            async () => {
                if (this.selectedDataPoint) {
                    const requestReady = this.requestStateSetup({
                        x: this.selectedDataPoint.x,
                        series: this.selectedDataPoint.series,
                        seriesIdentifier:
                            this.selectedDataPoint.seriesIdentifier,
                        xIdentifier: this.selectedDataPoint.xIdentifier,
                        xLabel: this.selectedDataPoint.xLabel,
                        metadata: this.selectedDataPoint.metadata,
                    });
                    if (!requestReady) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    const drilldownState = this.getDrilldownState();
                    if (!drilldownState) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    this.selectedDataDgStore.checkboxSelection = true;
                    const columns = this.resolveColumns(
                        this.getDrilldownDataEndpoint(source) as
                            | SignalDataEndpoint
                            | undefined,
                    );
                    this.selectedDataDgStore.setColumns(columns);
                    this.selectedDataDgStore.columns.forEach((column) => {
                        if (column.field === "timestamp") {
                            column.headerName =
                                this.applicationFiltersStore.quickFiltersStore.datePickerStore!.dateRangeFilterLabel;
                        }
                    });
                    const drilldownEndpoint =
                        this.getDrilldownDataEndpoint(source);
                    if (!drilldownEndpoint) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }
                    const res = await this.signalsService.getDrillDownForChart(
                        drilldownEndpoint,
                        drilldownState,
                        this.abortController.signal,
                    );

                    runInAction(() => {
                        this.handleDrilldownResponse({ res });
                    });
                }
            },
        );
    }

    async viewTopics() {
        const source = this.source;
        if (!source) return;
        this.selectedDataDgStore.setLoading(true);
        this.drilldownDialogTitle = this.getDrilldownTitle({
            source,
            isTopicsView: true,
        });
        this.drilldownSubtitle = this.getDrilldownSubtitle({
            source,
            isTopicsView: true,
        });
        this.lastDrilldownSource = source;

        this.showSelectedDataDialog = true;
        this.selectedDataDgStore.clearSelected();
        this.selectedDataDgStore.rows = [];

        return await this.setupAsyncTask(
            "Load Signals Drill Down",
            async () => {
                if (this.selectedDataPoint) {
                    const requestReady = this.requestStateSetup({
                        x: this.selectedDataPoint.x,
                        series: this.selectedDataPoint.series,
                        seriesIdentifier:
                            this.selectedDataPoint.seriesIdentifier,
                        xIdentifier: this.selectedDataPoint.xIdentifier,
                        xLabel: this.selectedDataPoint.xLabel,
                        metadata: this.selectedDataPoint.metadata,
                    });
                    if (!requestReady) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    const drilldownState = this.getDrilldownState();
                    if (!drilldownState) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    this.selectedDataDgStore.checkboxSelection = false;
                    this.selectedDataDgStore.setColumns(this.topicsColumns);
                    this.selectedDataDgStore.onCellClick = (params) => {
                        if (params.field === "name") {
                            const volume =
                                params.row.volume ?? params.row.count;
                            const topicCount =
                                volume !== undefined &&
                                volume !== null &&
                                !Number.isNaN(Number(volume))
                                    ? Number(volume)
                                    : null;

                            this.selectTopic({
                                selectedTopic: params.value as string,
                                topicNumber: params.row.topicNumber,
                                x: this.selectedDataPoint!.x,
                                series: this.selectedDataPoint!.series,
                                count: topicCount,
                            });
                            this.viewDataTopic();
                        }
                    };

                    const drilldownEndpoint =
                        this.getTopicsDrilldownEndpoint(source);
                    if (!drilldownEndpoint) {
                        this.selectedDataDgStore.setLoading(false);
                        return;
                    }

                    const res =
                        await this.signalsService.getTopicDrillDownForChart(
                            drilldownEndpoint,
                            drilldownState,
                            this.abortController.signal,
                        );

                    runInAction(() => {
                        this.handleTopicDrilldownResponse({ res });
                    });
                }
            },
        );
    }

    async viewInConvos() {
        const source = this.source;
        if (!source) return;
        const requestStateForConvos: ApplicationFilters =
            this.getDrilldownFilters({
                source,
            });

        this.applicationFiltersStore.currentlyAppliedFilters = _.cloneDeep(
            requestStateForConvos,
        );
    }

    async askKbo() {
        const source = this.source;
        if (!source) return;
        if (!this.selectedDataPoint && !this.selectedTopic && !this.selectedDriver)
            return;

        const selectedCount =
            this.selectedTopic?.count ??
            this.selectedDriver?.count ??
            this.selectedDataPoint?.count;

        if (
            selectedCount === 0 ||
            selectedCount === null ||
            selectedCount === undefined
        ) {
            // If there's 0 conversations for this data point, we should handle it
            this.agentStore.currentChatStore.handleSuggestion({
                content: "Analyze Data Point",
                action: () => {
                    this.agentStore.currentChatStore.analyzeDataPoint(
                        "", // empty count string, it won't be used
                    );
                },
                newChatName: "Analyze Data Point",
            });
        } else {
            // Get the filters for this data point
            const dataPointFilters = this.getDrilldownFilters({
                source,
            });
            if (
                (dataPointFilters.agentIds?.length ?? 0) > 0 &&
                this.applicationFilterValuesStore.agents.length === 0
            ) {
                await this.applicationFilterValuesStore.loadAgents();
            }
            // Managers load on store construction rather than on demand, so a data point opened
            // before that settles would otherwise name every manager "Unknown Agent Manager".
            if (
                (dataPointFilters.agentManagerIds?.length ?? 0) > 0 &&
                this.applicationFilterValuesStore.agentManagers.length === 0
            ) {
                await this.applicationFilterValuesStore.loadAgentManagers();
            }
            // Question result labels resolve from lazily cached module details.
            await loadQuestionResultLabelData(
                dataPointFilters.questionResults,
                this.applicationFilterValuesStore,
            );
            const dataPointContextMetadata =
                this.buildKboDataPointContextMetadata(
                    source,
                    dataPointFilters,
                    selectedCount,
                );

            // Make KBo do things (analyze and surface trends with the filters basically)
            this.agentStore.currentChatStore.handleSuggestion({
                content: "Analyze Data Point",
                action: () => {
                    this.agentStore.currentChatStore.analyzeDataPoint(
                        selectedCount ? selectedCount.toString() : " filtered ",
                        dataPointFilters,
                        dataPointContextMetadata,
                    );
                },
                newChatName: "Analyze Data Point",
            });
        }

        // Open drawer
        this.agentStore.openDrawer();
    }

    private buildKboDataPointContextMetadata(
        source: DrilldownSource,
        dataPointFilters: ApplicationFilters,
        selectedCount?: number | null,
    ): {
        chartId: string;
        chartTitle: string;
        endpointId?: string;
        contextSummary: string;
        dataPointQueryContext?: DataPointQueryContext;
    } {
        const chartId =
            (source as { id?: string }).id ||
            source.dataEndpoint?.toString() ||
            "Unknown";
        const normalizedTitle = source.title?.trim();
        const chartTitle = normalizedTitle || "Unknown";
        const chartDescription =
            chartTitle === "Unknown"
                ? "selected chart"
                : `${chartTitle} chart`;
        const countValue = Number(selectedCount);
        const countLabel =
            Number.isFinite(countValue) && countValue > 0
                ? `${countValue.toLocaleString()} ${
                      countValue === 1 ? "conversation" : "conversations"
                  }`
                : "selected conversations";
        const filterSummary = this.buildKboFiltersSummary(dataPointFilters);
        const config = getEndpointBehaviorConfig({
            dataEndpoint: source.dataEndpoint?.toString() ?? "",
        });
        const contextSuffix = config.kboContextSuffix?.({
            series: this.selectedDataPoint?.series ?? null,
            x: this.selectedDataPoint?.x ?? null,
            // Read from the request the data came from, so the wording matches the bars on screen.
            skillGrouping: source.state?.skillGrouping as SkillGrouping | undefined,
        });
        const basePrompt = filterSummary
            ? `Analyze the selected data point representing ${countLabel} from the ${chartDescription} using the following filters: ${filterSummary}.`
            : `Analyze the selected data point representing ${countLabel} from the ${chartDescription}.`;
        const contextSummary = contextSuffix
            ? `${basePrompt} ${contextSuffix}`
            : basePrompt;
        const dataPointQueryContext =
            this.buildStructuredKboDataPointContext(source);

        return {
            chartId,
            chartTitle,
            endpointId: source.dataEndpoint?.toString(),
            contextSummary,
            dataPointQueryContext,
        };
    }

    private buildStructuredKboDataPointContext(
        source: DrilldownSource,
    ): DataPointQueryContext | undefined {
        const endpoint = source.dataEndpoint?.toString() ?? "";

        if (endpoint === SignalsEndpoints.trends.AverageScoreByModuleOverTimeID) {
            const moduleId = this.resolveModuleIdForKboDataPoint(source);
            return moduleId ? { moduleId } : undefined;
        }

        if (
            endpoint === SignalsEndpoints.trends.AverageScoreByQuestionOverTimeID
        ) {
            const questionId = this.resolveQuestionIdForKboDataPoint(source);
            return questionId ? { questionId } : undefined;
        }

        if (
            endpoint ===
            SignalsEndpoints.eddyReports.AverageScoreByQuestionAndEddyOverTimeID
        ) {
            const questionId = source.selectedIds?.question;
            const context: DataPointQueryContext = {};
            if (typeof questionId === "string" && questionId) {
                context.questionId = questionId;
            }
            return context;
        }

        if (endpoint === SignalsEndpoints.eddyReports.AverageScoreByQuestionID) {
            const questionId =
                this.resolveQuestionIdByXForKboDataPoint(source);
            const context: DataPointQueryContext = {};
            if (questionId) {
                context.questionId = questionId;
            }
            return context;
        }

        return undefined;
    }

    private resolveQuestionIdByXForKboDataPoint(
        source: DrilldownSource,
    ): string | undefined {
        const selectedDataPoint = this.selectedDataPoint;
        if (!selectedDataPoint) {
            return undefined;
        }

        // The backend transform sets X_identifier to the question id, so prefer it.
        if (selectedDataPoint.xIdentifier) {
            return selectedDataPoint.xIdentifier;
        }

        const selectedModuleId = source.selectedIds?.module;
        if (!selectedModuleId) {
            return undefined;
        }

        return this.optionsStore.orgModules
            .find((module) => module.id === selectedModuleId)
            ?.questions.find(
                (question) => question.shortQuestionText === selectedDataPoint.x,
            )?.id;
    }

    private resolveModuleIdForKboDataPoint(
        source: DrilldownSource,
    ): string | undefined {
        const selectedSeriesIdentifier = this.selectedDataPoint?.seriesIdentifier;
        if (selectedSeriesIdentifier) {
            return selectedSeriesIdentifier;
        }

        const selectedDataPoint = this.selectedDataPoint;
        if (!selectedDataPoint) {
            return undefined;
        }

        const moduleIdFromData = source.data?.find(
            (datum: {
                x?: string | number | null;
                grouping?: string | null;
                grouping_identifier?: string | number | null;
            }) =>
                datum.x === selectedDataPoint.x &&
                datum.grouping === selectedDataPoint.series,
        )?.grouping_identifier;
        if (moduleIdFromData) {
            return `${moduleIdFromData}`;
        }

        return [...this.optionsStore.orgScoredModules, ...this.optionsStore.orgModules]
            .find(
                (module) =>
                    module.displayName === selectedDataPoint.series ||
                    module.name === selectedDataPoint.series,
            )
            ?.id;
    }

    private resolveQuestionIdForKboDataPoint(
        source: DrilldownSource,
    ): string | undefined {
        const selectedDataPoint = this.selectedDataPoint;
        const selectedModuleId = source.selectedIds?.module;
        if (!selectedDataPoint || !selectedModuleId) {
            return undefined;
        }

        return this.optionsStore.orgModules
            .find((module) => module.id === selectedModuleId)
            ?.questions.find(
                (question) => {
                    const displayName = (question as unknown as {
                        displayName?: string;
                    }).displayName;

                    return (
                    question.shortQuestionText === selectedDataPoint.series ||
                    question.questionText === selectedDataPoint.series ||
                    displayName === selectedDataPoint.series
                    );
                },
            )?.id;
    }

    private buildKboFiltersSummary(filters: ApplicationFilters): string {
        const orderedEntries = this.getOrderedFilterEntries(filters);
        if (orderedEntries.length === 0) {
            return "";
        }

        if (orderedEntries.length === 1) {
            return orderedEntries[0];
        }

        if (orderedEntries.length === 2) {
            return `${orderedEntries[0]} and ${orderedEntries[1]}`;
        }

        return `${orderedEntries.slice(0, -1).join(", ")}, and ${orderedEntries[orderedEntries.length - 1]}`;
    }

    private getOrderedFilterEntries(filters: ApplicationFilters): string[] {
        const entries: string[] = [];
        const filterItems = this.applicationFilterItemsStore.filterGroups.flatMap(
            (group) => group.items,
        );

        for (const item of filterItems) {
            const formatted = this.getFormattedFilterValue(filters, item.filterKey);
            if (!formatted) continue;

            // Plain string entries, so the two identically-titled Theme filters are qualified by
            // source. See ApplicationFilterItemsStore.plainTextFilterTitleByKey.
            const title =
                this.getSingularFilterTitle(filters, item.filterKey) ??
                this.applicationFilterItemsStore.plainTextFilterTitleByKey.get(
                    item.filterKey,
                ) ??
                item.title;

            entries.push(`${title}: ${formatted}`);
        }

        return entries;
    }

    /**
     * The singular title for a filter narrowed to exactly one value, so the prompt reads
     * "Agent Manager: Jane Doe" rather than "Agent Managers: Jane Doe". Undefined when the filter
     * has no singular form or holds more than one value, leaving the plural title to stand.
     */
    private getSingularFilterTitle(
        filters: ApplicationFilters,
        filterKey: FilterKey,
    ): string | undefined {
        const singularTitle = SINGULAR_FILTER_TITLE_BY_KEY[filterKey as string];
        if (!singularTitle) return undefined;

        const value = _.get(filters, filterKey as string);
        return Array.isArray(value) && value.length === 1
            ? singularTitle
            : undefined;
    }

    private getFormattedFilterValue(
        filters: ApplicationFilters,
        filterKey: FilterKey,
    ): string | undefined {
        if (filterKey === "dateRange") {
            if (!filters.startDate || !filters.endDate) return undefined;

            const startDate = moment(filters.startDate);
            const endDate = moment(filters.endDate);
            if (!startDate.isValid() || !endDate.isValid()) return undefined;

            if (startDate.isSame(endDate, "day")) {
                return startDate.format("MMM D, YYYY");
            }

            if (startDate.isSame(endDate, "month")) {
                return `${startDate.format("MMM D")}\u2013${endDate.format("D, YYYY")}`;
            }

            if (startDate.isSame(endDate, "year")) {
                return `${startDate.format("MMM D")}\u2013${endDate.format("MMM D, YYYY")}`;
            }

            return `${startDate.format("MMM D, YYYY")}\u2013${endDate.format("MMM D, YYYY")}`;
        }

        if (filterKey === "dateReferenceOption") {
            if (filters.dateReferenceOption === DateReferenceOption.InteractionDate) {
                return undefined;
            }
            return `${filters.dateReferenceOption}`
                .replace(/Date$/g, "")
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .trim();
        }

        if (filterKey === "callDuration") {
            const minDuration = filters.minCallDuration ?? 0;
            const maxDuration = filters.maxCallDuration ?? 0;
            if (minDuration <= 0 && maxDuration <= 0) return undefined;
            if (isOpenEndedCallDuration(minDuration, maxDuration))
                return `${minDuration}+ minutes`;
            return `${minDuration} - ${maxDuration > 0 ? maxDuration : "Any"} minutes`;
        }

        if (filterKey === "messageCount") {
            const minCount = filters.minMessageCount ?? 0;
            const maxCount = filters.maxMessageCount ?? 0;
            if (minCount <= 0 && maxCount <= 0) return undefined;
            return `${minCount} - ${maxCount > 0 ? maxCount : "Any"} messages`;
        }

        if (typeof filterKey === "string" && filterKey.startsWith("extendedMetadata.")) {
            const metadataKey = filterKey.replace("extendedMetadata.", "");
            const groupedValue = filters.extendedMetadataGrouped?.[metadataKey];
            if (
                groupedValue?.values &&
                groupedValue.values.length > 0
            ) {
                const groupedLabel = groupedValue.contains
                    ? groupedValue.values.join(", ")
                    : `excludes ${groupedValue.values.join(", ")}`;
                return groupedLabel;
            }

            const metadataValue = filters.extendedMetadata?.[metadataKey];
            return metadataValue?.trim() || undefined;
        }

        const rawValue = _.get(filters, filterKey as string);
        if (rawValue === undefined || rawValue === null) return undefined;
        if (typeof rawValue === "string" && rawValue.trim().length === 0) {
            return undefined;
        }
        if (isFilterValueEmpty(rawValue)) return undefined;

        switch (filterKey) {
            case "callDirections":
                return this.formatListForSummary(
                    (rawValue as CallDirection[])
                        .map((callDirection) =>
                            CallDirection.toString(callDirection),
                        )
                        .filter(Boolean),
                );
            case "evaluationTypes":
                return this.formatListForSummary(
                    (rawValue as EvaluationType[])
                        .map((evaluationType) =>
                            EvaluationType.toString(evaluationType),
                        )
                        .filter(Boolean),
                );
            case "mediaTypes":
                return this.formatListForSummary(
                    (rawValue as MediaType[])
                        .map((mediaType) => MediaType.toString(mediaType))
                        .filter(Boolean),
                );
            case "beginningSentiment":
            case "endingSentiment":
                return Sentiment.toString(rawValue as Sentiment);
            case "hipaaCompliance":
                return HipaaCompliance.toString(rawValue as HipaaCompliance);
            case "safetyEvent":
                return SafetyEventAsString(rawValue as any);
            case "topics":
                return this.formatListForSummary(
                    (rawValue as SimpleTopicCluster[])
                        .map((topic) => topic.topicLabel)
                        .filter(Boolean),
                );
            case "themes":
                return this.formatListForSummary(
                    (rawValue as SimpleTheme[])
                        .map((theme) => theme.themeLabel)
                        .filter(Boolean),
                );
            case "primaryTopics":
                return this.formatListForSummary(
                    (rawValue as PrimaryTopic[])
                        .map((topic) => topic.topicLabel)
                        .filter(Boolean),
                );
            case "drivers":
                return this.formatListForSummary(
                    (rawValue as PrimaryDriver[])
                        .map((driver) => driver.driverName)
                        .filter(Boolean),
                );
            case "modelResults":
                return this.formatListForSummary(
                    (rawValue as Array<{ name: string; results: string[] }>)
                        .map((result) => {
                            const values = this.formatListForSummary(
                                (result.results ?? []).map((entry) => `${entry}`),
                                3,
                            );
                            return `${result.name}: ${values}`;
                        })
                        .filter(Boolean),
                );
            case "rbcFilterItems":
                const classifierNameById = new Map<string, string>();
                [
                    ...(this.applicationFilterValuesStore.userClassifiers ?? []),
                    ...(this.optionsStore.userClassifiers ?? []),
                ].forEach((classifier) => {
                    const classifierId = `${classifier.id ?? ""}`;
                    const classifierName = classifier.name?.trim();
                    if (!classifierId || !classifierName) return;
                    if (!classifierNameById.has(classifierId)) {
                        classifierNameById.set(classifierId, classifierName);
                    }
                });

                return (
                    rawValue as Array<{
                        value?: boolean;
                        classifierIds?: string[];
                    }>
                )
                    .flatMap((item) =>
                        (item.classifierIds ?? []).map((classifierId) =>
                            item.value === false
                                ? `Exclude ${classifierNameById.get(`${classifierId}`) || "Unknown classifier"}`
                                : classifierNameById.get(`${classifierId}`) ||
                                  "Unknown classifier",
                        ),
                    )
                    .join(", ");
            case "languages":
                return this.formatListForSummary(
                    (rawValue as LanguageFilterObject[])
                        .map((language) => {
                            const option =
                                this.applicationFiltersStore.languageOptions.find(
                                    (item) =>
                                        item.value === language.languageValue,
                                );
                            return option?.label ?? `${language.languageValue}`;
                        })
                        .filter(Boolean),
                );
            case "dataLabels":
                return this.formatListForSummary(
                    (rawValue as Array<{ name: string }>)
                        .map((label) => label.name)
                        .filter(Boolean),
                );
            case "agentIds": {
                const agentNameById = new Map(
                    (this.applicationFilterValuesStore.agents ?? []).map(
                        (agent) => [
                            `${agent.id ?? ""}`,
                            agent.fullName?.trim() ||
                                agent.agentEmail ||
                                "Unknown Agent",
                        ],
                    ),
                );

                return this.formatListForSummary(
                    (rawValue as string[])
                        .map(
                            (agentId) =>
                                agentNameById.get(`${agentId}`) ||
                                "Unknown Agent",
                        )
                        .filter(Boolean),
                );
            }
            case "agentManagerIds": {
                // The filter stores manager user ids, which mean nothing to KBo on their own, so
                // they are resolved to full names the same way agents are.
                const managerNameById = new Map(
                    (
                        this.applicationFilterValuesStore.agentManagers ?? []
                    ).map((manager) => [
                        `${manager.id ?? ""}`,
                        manager.fullName?.trim() ||
                            "Unknown Agent Manager",
                    ]),
                );

                return this.formatListForSummary(
                    (rawValue as string[])
                        .map(
                            (managerId) =>
                                managerNameById.get(`${managerId}`) ||
                                "Unknown Agent Manager",
                        )
                        .filter(Boolean),
                );
            }
            case "contactTypes":
                return this.formatListForSummary(
                    (rawValue as string[])
                        .map((contactType) =>
                            this.applicationFilterValuesStore.getContactTypeLabel(
                                contactType,
                            ),
                        )
                        .filter(Boolean),
                );
            case "outlineSegmentFilterItems":
                return this.formatListForSummary(
                    (
                        rawValue as Array<{
                            value?: boolean;
                            segments?: string[];
                        }>
                    )
                        .flatMap((item) =>
                            (item.segments ?? []).map((segment) =>
                                item.value === false
                                    ? `NOT ${formatSegmentHeading(segment)}`
                                    : formatSegmentHeading(segment),
                            ),
                        )
                        .filter(Boolean),
                );
            case "patientJourneyStages":
                return this.formatListForSummary(
                    (rawValue as number[])
                        .map((stageValue) =>
                            this.applicationFiltersStore.journeyStageOptions.find(
                                (option) => option.value === stageValue,
                            )?.label,
                        )
                        .filter((label): label is string => !!label),
                );
            case "hierarchyIds": {
                const hierarchyCount = (rawValue as string[]).length;
                if (!hierarchyCount) return undefined;
                return hierarchyCount === 1
                    ? "1 selected"
                    : `${hierarchyCount} selected`;
            }
            case "favoriteConversations":
                return rawValue ? "Yes" : "No";
            case "eddyEffectStatus":
                return rawValue ? "Present" : "Not Present";
            case "moduleScores":
                return this.formatListForSummary(
                    (rawValue as ModuleScore[])
                        .map((moduleScore) => {
                            const moduleName =
                                this.applicationFilterValuesStore.hierarchyLicensedModules?.find(
                                    (module) =>
                                        module.id ===
                                        moduleScore.licensedModuleId,
                                )?.title ?? "Unknown Module";
                            const operatorLabel =
                                this.getOperatorLabelForSummary(
                                    moduleScore.operator,
                                );
                            return `${moduleName} ${operatorLabel} ${moduleScore.moduleScore}%`;
                        })
                        .filter(Boolean),
                    3,
                );
            case "questionResults":
                return this.formatListForSummary(
                    (rawValue as QuestionResultFilter[])
                        .filter(
                            (questionResult) =>
                                questionResult.tagIds.length > 0,
                        )
                        .map((questionResult) => {
                            const { moduleLabel, questionLabel, tagLabels } =
                                getQuestionResultLabelParts(
                                    questionResult,
                                    this.applicationFilterValuesStore,
                                );
                            return `${moduleLabel} - ${questionLabel}: ${tagLabels.join(", ")}`;
                        })
                        .filter(Boolean),
                    3,
                );
            default:
                if (Array.isArray(rawValue)) {
                    if (rawValue.every((entry) => typeof entry !== "object")) {
                        return this.formatListForSummary(
                            rawValue.map((entry) => `${entry}`),
                        );
                    }
                    const asNamedEntries = rawValue
                        .map((entry) =>
                            typeof entry === "object" && entry !== null
                                ? (entry as { name?: string }).name
                                : undefined,
                        )
                        .filter((name): name is string => !!name);
                    if (asNamedEntries.length > 0) {
                        return this.formatListForSummary(asNamedEntries);
                    }
                    return JSON.stringify(rawValue);
                }

                if (typeof rawValue === "boolean") {
                    return rawValue ? "Yes" : "No";
                }

                if (typeof rawValue === "object") {
                    return JSON.stringify(rawValue);
                }

                return `${rawValue}`;
        }
    }

    private getOperatorLabelForSummary(operator?: Operator): string {
        switch (operator) {
            case Operator.Gt:
                return ">";
            case Operator.GtEq:
                return ">=";
            case Operator.Lt:
                return "<";
            case Operator.LtEq:
                return "<=";
            case Operator.NEq:
                return "!=";
            case Operator.Eq:
                return "=";
            case Operator.In:
                return "in";
            case Operator.Between:
                return "between";
            default:
                return "=";
        }
    }

    private formatListForSummary(values: string[], maxItems?: number): string {
        const cleanedValues = values
            .map((value) => `${value}`.trim())
            .filter((value) => value.length > 0);
        if (cleanedValues.length === 0) {
            return "";
        }

        const max =
            maxItems ?? DrilldownStore.MAX_FILTER_VALUES_IN_SUMMARY;
        if (cleanedValues.length <= max) {
            return cleanedValues.join(", ");
        }

        const preview = cleanedValues.slice(0, max).join(", ");
        return `${preview} (+${cleanedValues.length - max} more)`;
    }

    /** Helpers */

    /**
     * Derive the drilldown dialog title based on the current selection, mirroring SignalsV1 logic.
     */
    getDrilldownTitle = ({
        source = this.source,
        isTopicsView = false,
    }: {
        source?: DrilldownSource | null;
        isTopicsView?: boolean;
    }): string => {
        if (this.selectedTopic) {
            return `Conversations for ${this.selectedTopic.selectedTopic}`;
        }

        if (this.selectedDriver) {
            return `Conversations for ${this.selectedDriver.selectedDriver}`;
        }

        if (isTopicsView) {
            return resolveAggregationDrilldownTitle({
                selectedDataPoint: this.selectedDataPoint,
                aggregationLabels: [
                    ...(source?.themes?.map((theme) => theme.themeLabel) ?? []),
                    ...(source?.topics?.map((topic) => topic.topicLabel) ?? []),
                ],
                fallbackTitle: source?.title,
            });
        }

        const normalizedEndpoint = normalizeEndpointForDrilldown(
            source?.dataEndpoint,
        );

        if (
            normalizedEndpoint === SignalsEndpoints.trends.QualityScoreOverTimeID &&
            this.selectedDataPoint?.y !== undefined &&
            this.selectedDataPoint.y !== null
        ) {
            const qualityPercent = Number(this.selectedDataPoint.y);

            if (!Number.isNaN(qualityPercent)) {
                return `Quality Modules averaging ${qualityPercent.toFixed(1)}%`;
            }
        }

        // Prefer the most specific label from the selected data point.
        const preferSeries =
            source?.config?.renderOptions?.preferSeriesInTooltip ?? false;
        const primaryLabel = preferSeries
            ? this.selectedDataPoint?.series ?? this.selectedDataPoint?.x
            : this.selectedDataPoint?.x ?? this.selectedDataPoint?.series;

        if (
            primaryLabel !== undefined &&
            primaryLabel !== null &&
            primaryLabel !== ""
        ) {
            if (
                (normalizedEndpoint ===
                    SignalsEndpoints.trends.AverageScoreByModuleOverTimeID ||
                    normalizedEndpoint ===
                        SignalsEndpoints.trends
                            .AverageScoreByQuestionOverTimeID ||
                    normalizedEndpoint ===
                        SignalsEndpoints.trends.AverageScoreBySkillID) &&
                this.selectedDataPoint?.y !== undefined &&
                this.selectedDataPoint.y !== null
            ) {
                const percent = Number(this.selectedDataPoint.y);

                if (!Number.isNaN(percent)) {
                    return `${primaryLabel} averaging ${percent.toFixed(1)}%`;
                }
            }

            return `${primaryLabel}`;
        }
        return source?.title ?? "Drilldown";
    };

    /**
     * Derive the drilldown subtitle. For temporal charts we show the clicked date range;
     * otherwise we show the x/series label, matching SignalsV1 behaviour.
     */
    getDrilldownSubtitle = ({
        source = this.source,
        isTopicsView = false,
    }: {
        source?: DrilldownSource | null;
        isTopicsView?: boolean;
    }): string => {
        if (this.selectedTopic || this.selectedDriver || isTopicsView) {
            // Topic and Driver drilldowns show no subtitle; the aggregation lists set their own
            // subtitle after data load.
            return "";
        }

        if (!this.selectedDataPoint || !source) return "";

        const { x, series } = this.selectedDataPoint;
        const seriesText = series ? ` and ${series}` : "";

        const isTemporal = source.config?.renderOptions?.isTemporal ?? false;
        const temporalGrain = source.temporalGrain;

        if (isTemporal && temporalGrain && x !== undefined && x !== null) {
            const { startDate, endDate } = this.getDrilldownDateBounds(source);
            const dateLabel = getXDateRangeLabel(
                x,
                temporalGrain,
                startDate,
                endDate,
            );
            return `Data for ${dateLabel}${seriesText}`;
        }

        if (x !== undefined && x !== null) {
            return `Data for ${x}${seriesText}`;
        }

        return "";
    };

    handleDrilldownResponse = ({ res }: { res: ISignalsDrilldownResponse }) => {
        this.drilldownSql = res.sqlQuery;

        this.selectedDataDgStore.onPaginationModelChange = (
            model: GridPaginationModel,
        ) => {
            this.selectedDataDgStore.paginationModel = model;

            this.viewData();
        };

        this.selectedDataDgStore.rows = res.data.map((item) => ({
            ...item,
            id: item.audioMetadataId,
            topics: this.getTopicsFromClassifiers(item.classifiers),
            agentFullName:
                !item.agentFirstName && !item.agentLastName
                    ? "Unknown Agent"
                    : item.agentFirstName + " " + item.agentLastName,
        }));
        this.selectedDataDgStore.rowCount = res.totalCount;
        this.selectedDataDgStore.paginationSize = res.pageSize;

        this.selectedDataDgStore.setLoading(false);
    };

    handleTopicDrilldownResponse = ({
        res,
    }: {
        res: ISignalsTopicsDrilldownResponse;
    }) => {
        this.drilldownSql = res.sqlQuery;
        this.selectedDataDgStore.onPaginationModelChange = (
            model: GridPaginationModel,
        ) => {
            this.selectedDataDgStore.paginationModel = model;

            if (this.lastDrilldownSource) {
                this.viewTopics();
            }
        };

        this.selectedDataDgStore.rows = res.data.map((item) => ({
            ...item,
            id: uuidv4(),
        }));
        this.selectedDataDgStore.rowCount = res.totalCount;
        this.selectedDataDgStore.paginationSize = res.pageSize;
        this.drilldownSubtitle = res.totalCount + " Topics";

        this.selectedDataDgStore.setLoading(false);
    };

    handleDriverDrilldownResponse = ({
        res,
    }: {
        res: ISignalsDriversDrilldownResponse;
    }) => {
        this.drilldownSql = res.sqlQuery;
        this.selectedDataDgStore.onPaginationModelChange = (
            model: GridPaginationModel,
        ) => {
            this.selectedDataDgStore.paginationModel = model;

            if (this.lastDrilldownSource) {
                this.viewDrivers();
            }
        };

        this.selectedDataDgStore.rows = res.data.map((item) => ({
            ...item,
            id: uuidv4(),
        }));
        this.selectedDataDgStore.rowCount = res.totalCount;
        this.selectedDataDgStore.paginationSize = res.pageSize;
        this.drilldownSubtitle = res.totalCount + " Drivers";

        this.selectedDataDgStore.setLoading(false);
    };

    /*
     * This function will get the filters for a drilldown but will NOT set them. Saves them into 'after-drilldown' and saves the previous state as 'before-drilldown'
     * @returns ApplicationFilters object that can be used to set filters in another store
     */
    getDrilldownFilters = ({
        source = this.source,
    }: {
        source?: DrilldownSource | null;
    }) => {
        const isTopics = !!this.selectedTopic;
        const isDrivers = !this.selectedTopic && !!this.selectedDriver;
        if (!source) {
            return toJS(this.applicationFiltersStore.toFilterObject());
        }
        if (!isTopics && !isDrivers && !this.selectedDataPoint) {
            return this.getBaseFiltersForDrilldown(source);
        }
        this.applicationFiltersStore.saveCurrentFilterState("before-drilldown");

        const aggregationSelection = isTopics
            ? this.selectedTopic!
            : isDrivers
            ? this.selectedDriver!
            : null;

        const requestStateForConvos: ApplicationFilters = this.filtersSetup({
            source,
            x: aggregationSelection
                ? aggregationSelection.x
                : this.selectedDataPoint!.x,
            series: aggregationSelection
                ? aggregationSelection.series
                : this.selectedDataPoint!.series,
            seriesIdentifier: this.selectedDataPoint?.seriesIdentifier,
            xIdentifier: this.selectedDataPoint?.xIdentifier,
            xLabel: this.selectedDataPoint?.xLabel,
            metadata: this.selectedDataPoint?.metadata,
        });

        this.applicationFiltersStore.saveExternalFilterState(
            "after-drilldown",
            requestStateForConvos,
        );
        return requestStateForConvos;
    };

    getTopicsFromClassifiers(
        classifiersFromDrilldown: IClassifiersFromDrilldown[],
    ): Topics | null {
        const topicsClassifier: IClassifiersFromDrilldown | undefined =
            classifiersFromDrilldown.find(
                (classifier: IClassifiersFromDrilldown) =>
                    classifier.result &&
                    classifier.classifierName === ML_Topic_LLM,
            );

        if (topicsClassifier) {
            const result = JSON.parse(topicsClassifier.result);
            const topics: Topics = {
                topic1:
                    result["Topic 1"] && result["Topic 1"] !== "NA"
                        ? result["Topic 1"]
                        : null,
                topic2:
                    result["Topic 2"] && result["Topic 2"] !== "NA"
                        ? result["Topic 2"]
                        : null,
                topic3:
                    result["Topic 3"] && result["Topic 3"] !== "NA"
                        ? result["Topic 3"]
                        : null,
                topic1Reasoning:
                    (result["Topic 1"] &&
                        result["Topic 1 Reasoning"] &&
                        result["Topic 1"]) !== "NA"
                        ? result["Topic 1 Reasoning"]
                        : null,
                topic2Reasoning:
                    (result["Topic 2"] &&
                        result["Topic 2 Reasoning"] &&
                        result["Topic 2"]) !== "NA"
                        ? result["Topic 2 Reasoning"]
                        : null,
                topic3Reasoning:
                    (result["Topic 3"] &&
                        result["Topic 3 Reasoning"] &&
                        result["Topic 3"]) !== "NA"
                        ? result["Topic 3 Reasoning"]
                        : null,
            };
            return topics;
        } else {
            return null;
        }
    }

    private getDrilldownDateBounds(source: DrilldownSource | null = this.source) {
        const requestStartDate = source?.state?.startDate;
        const requestEndDate = source?.state?.endDate;

        const parsedStartDate = requestStartDate
            ? moment(requestStartDate)
            : undefined;
        const parsedEndDate = requestEndDate ? moment(requestEndDate) : undefined;

        if (parsedStartDate?.isValid() && parsedEndDate?.isValid()) {
            return {
                startDate: parsedStartDate,
                endDate: parsedEndDate,
            };
        }

        return {
            startDate: this.applicationFiltersStore.quickFiltersStore.startDate,
            endDate: this.applicationFiltersStore.quickFiltersStore.endDate,
        };
    }

    /**
     * The date range a drilldown should narrow to when the clicked point belongs to a date bucket.
     *
     * The bucket is the population the user pointed at, so every drilldown out of a date-grouped
     * chart has to carry the bucket's range or it reports on the chart's whole range instead.
     * Returns undefined for charts grouped by anything else, leaving the existing paths untouched.
     *
     * `getDateBucketSpan` narrows the span to the chart's own range, since a chart's first and last
     * buckets are usually partial.
     *
     * `DateReferenceOption` comes back alongside because buckets are cut on the interaction date, so
     * the drilldown has to ask for interaction dates or it answers with a different population than
     * the bar it came from.
     */
    private getDateGroupingDateRange = (
        source: DrilldownSource | null,
        selection: DrilldownSelection,
    ):
        | { beginDate: string; endDate: string; dateReference: DateReferenceOption }
        | undefined => {
        if (!source) return undefined;

        const dateGrouping = getDateGroupingSelection({ source, selection });
        if (!dateGrouping) return undefined;

        const { startDate, endDate } = this.getDrilldownDateBounds(source);
        // Only an auto grain needs the chart's bounds. Without them there is no range to size a
        // bucket from, so the narrowest grain is the one that cannot over-report.
        const grain = resolveDateGroupingTemporalGrain(dateGrouping.grain, () =>
            startDate && endDate ? temporalGrain(startDate, endDate) : "Daily",
        );

        // The chart's range is passed in so a partial edge bucket narrows to the part the chart
        // actually counted, which is what keeps the drilldown's total equal to the bar's.
        const span = getDateBucketSpan(dateGrouping.bucketStart, grain, {
            start: startDate,
            end: endDate,
        });
        if (!span) return undefined;

        return {
            beginDate: span.start.toISOString(),
            endDate: span.end.toISOString(),
            dateReference: DateReferenceOption.InteractionDate,
        };
    };

    /**
     * The call-duration bounds a drilldown should narrow to when the clicked point belongs to a
     * duration bucket.
     *
     * The range counterpart to <see cref="getDateGroupingDateRange"/>: the bucket is the population
     * the user pointed at, so a drilldown out of one has to carry its bounds or it reports on every
     * duration the chart covered. Returns undefined for charts not grouped by call duration, which
     * leaves the existing paths untouched.
     *
     * The bounds map straight onto the duration filter, whose maximum is exclusive for exactly this
     * reason: "2-5 min" holds 2:00.000 through 4:59.999, and an inclusive 5 would also match the
     * millisecond value that opens the next bar.
     *
     * The last bucket has no upper bound of its own and takes the top of the duration filter's
     * range instead, 200 minutes being the highest bound that filter can express. The chart is
     * capped at the same 200, so a call over that is in neither the bar nor the drilldown and the
     * two still hold the same conversations. `20+ min` is a label for 20 to 200, chosen because it
     * reads better than the literal range, not because the bucket is open-ended.
     */
    private getCallDurationGroupingFilters = (
        source: DrilldownSource | null,
        selection: DrilldownSelection,
    ): { minCallDuration: number; maxCallDuration?: number } | undefined => {
        const bounds = this.getRangeGroupingBucketBounds(
            source,
            selection,
            "CallDuration",
            MAX_CALL_DURATION,
        );
        if (!bounds) return undefined;

        return {
            minCallDuration: bounds.lowerBound,
            maxCallDuration: bounds.upperBound,
        };
    };

    /**
     * The message-count bounds a drilldown should narrow to when the clicked point belongs to a
     * message-count bucket. The discrete sibling of the duration bounds above, and the difference
     * between them is the whole of what "discrete" means here.
     *
     * A message count cannot fall between two whole numbers, so its buckets do not share a boundary:
     * "6-10 messages" holds 6 through 10 and the next bar starts at 11. Both ends of the bucket are
     * therefore values a conversation can actually have, which is exactly what the message-count
     * filter pair already takes. No exclusive sibling is needed the way call duration needed one,
     * because `>= 6 AND <= 10` *is* the bucket.
     *
     * The last bucket takes the top of the message count filter's own range, 200, the same way the
     * duration one takes 200 minutes: that is the highest bound the filter can express and what it
     * shows as "31+ messages", so the drilldown and a range the user sets by hand are one filter
     * rather than two that look alike. The chart is capped at the same 200, so a conversation above
     * it is in neither the bar nor the drilldown and the two still hold the same conversations.
     * `31+ messages` is a label for 31 to 200, chosen because it reads better than the literal
     * range, not because the bucket is open-ended.
     */
    private getMessageCountGroupingFilters = (
        source: DrilldownSource | null,
        selection: DrilldownSelection,
    ): { minMessageCount: number; maxMessageCount?: number } | undefined => {
        const bounds = this.getRangeGroupingBucketBounds(
            source,
            selection,
            "MessageCount",
            MAX_MESSAGE_COUNT,
        );
        if (!bounds) return undefined;

        return {
            minMessageCount: bounds.lowerBound,
            maxMessageCount: bounds.upperBound,
        };
    };

    /**
     * The module score conditions a drilldown should narrow to when the clicked point belongs to a
     * score bucket.
     *
     * The third range dimension, and the one whose bounds do not travel as a min/max pair. A score
     * belongs to one module, so the filter is a list of conditions each naming its module, and a
     * bucket is two of them ANDed: `>= 80` and `< 85`. That is the pair the module score filter has
     * taken since AUT-10582, so a drilldown and a range the user builds by hand are one filter
     * rather than two that look alike.
     *
     * The last bucket closes on 100 inclusively rather than running open-ended. A score cannot pass
     * 100, so `<= 100` excludes nothing the bar counted, including a perfect score, and it makes
     * the filter say the range its bar is labelled with: "95-100%" in both places, in the chip and
     * in the Smart Banner as well as on the axis. That is the same reason message count states its
     * own top inclusively; the top of a scale is the highest value inside it.
     *
     * Returns undefined for charts not grouped by module score, which leaves the existing paths
     * untouched.
     */
    private getModuleScoreGroupingFilters = (
        source: DrilldownSource | null,
        selection: DrilldownSelection,
    ): ModuleScore[] | undefined => {
        if (!source) return undefined;

        const grouping = getNumericRangeGroupingSelection({
            source,
            selection,
            optionsStore: this.optionsStore,
        });
        if (!grouping || grouping.option !== "ModuleScore") return undefined;

        const licensedModuleId = grouping.moduleId;
        if (!licensedModuleId) return undefined;

        const bounds = getNumericRangeBucketBounds(
            grouping.cutPoints,
            grouping.bucketIndex,
            grouping.mode,
        );
        if (!bounds) return undefined;

        const conditions: ModuleScore[] = [
            {
                licensedModuleId,
                operator: Operator.GtEq,
                moduleScore: bounds.lowerBound,
            },
        ];

        // Strictly less than, because a continuous bucket excludes the boundary it stops at: a
        // score of exactly 85 is in the bar above, so an inclusive bound would return it here too.
        // The last bucket is the exception, and inclusive for the opposite reason: its bound is the
        // top of the scale, so 100 is inside it.
        conditions.push(
            bounds.upperBound !== undefined
                ? {
                      licensedModuleId,
                      operator: Operator.Lt,
                      moduleScore: bounds.upperBound,
                  }
                : {
                      licensedModuleId,
                      operator: Operator.LtEq,
                      moduleScore: MODULE_SCORE_MAX,
                  },
        );

        return conditions;
    };

    /**
     * The bucket a selection landed on, when the chart is grouped by the named range dimension.
     *
     * Shared by the two range dimensions because everything up to this point is the same for both:
     * read the bucket's ordinal off the selection, check it belongs to the dimension asked for, turn
     * it into bounds, and close the last bucket's open end at the top of the dimension's own filter.
     * What differs is only which filter pair the bounds are written to, and whether the upper one is
     * compared inclusively, which is the caller's to know, since it is the caller that names the
     * dimension.
     *
     * `ceiling` is the top of that filter's scale, which is what a "+" range is stored as: "31+
     * messages" travels as 31-200 rather than as a lower bound with nothing above it, so a drilldown
     * and a range the user drags to the end of the track are the same filter. A last bucket starting
     * at or above the ceiling has no bound to take and keeps none, rather than asking for a range
     * that ends before it begins. Both dimensions reject cut points at or above their own ceiling,
     * so that branch is unreachable today and kept for a range dimension that has none.
     */
    private getRangeGroupingBucketBounds = (
        source: DrilldownSource | null,
        selection: DrilldownSelection,
        option: NumericRangeGroupByOption,
        ceiling: number,
    ): { lowerBound: number; upperBound?: number } | undefined => {
        if (!source) return undefined;

        const grouping = getNumericRangeGroupingSelection({
            source,
            selection,
            optionsStore: this.optionsStore,
        });
        if (!grouping || grouping.option !== option) return undefined;

        const bounds = getNumericRangeBucketBounds(
            grouping.cutPoints,
            grouping.bucketIndex,
            grouping.mode,
        );
        if (!bounds) return undefined;

        return {
            lowerBound: bounds.lowerBound,
            upperBound:
                bounds.upperBound ??
                (bounds.lowerBound < ceiling ? ceiling : undefined),
        };
    };

    getDrilldownDateRange = (
        startDateX,
        tempGrain: SignalsTemporalLabels,
        source: DrilldownSource | null = this.source,
    ): {
        beginDate?: string;
        endDate?: string;
    } => {
        const { startDate, endDate } = this.getDrilldownDateBounds(source);
        const dateString = getXDateRangeLabel(
            startDateX,
            tempGrain,
            startDate,
            endDate,
        );

        // dateString is returned as "FIRST_DAY - LAST_DAY"

        const datesArray = dateString.split("-");

        let firstDay = moment(datesArray[0], "MMM D, YYYY");
        let lastDay = moment(datesArray[1], "MMM D, YYYY").endOf("d");

        // for some edge cases for weekly/monthly grains there is a single day, so we need to check if lastDay exists
        if (tempGrain === "Daily" || !datesArray[1]) {
            const startDateCopy = moment(startDate);
            firstDay =
                moment(startDateX) < startDateCopy
                    ? startDateCopy
                    : moment(startDateX);
            lastDay = moment(firstDay).endOf("d");
        }

        return toIsoByDateReference(
            firstDay,
            lastDay,
            this.applicationFiltersStore.dateReferenceOption,
        );
    };

    /** Set up the drilldown request state using the local copy of the source state */
    requestStateSetup = ({
        x,
        series,
        seriesIdentifier,
        xIdentifier,
        xLabel,
        metadata,
    }: {
        x?: string | number | null;
        series?: string | null;
        seriesIdentifier?: string;
        xIdentifier?: string;
        xLabel?: string;
        metadata?: { name: string; value: unknown }[];
    }): boolean => {
        const source = this.source;
        if (!source) {
            return false;
        }

        const drilldownState = this.getDrilldownState();
        if (!drilldownState) {
            return false;
        }

        const strategy = this.getStrategy(source);
        const missingCapabilities = findMissingCapabilities(strategy, source);
        if (missingCapabilities.length > 0) {
            return false;
        }

        const selection = {
            x,
            series,
            seriesIdentifier,
            xIdentifier,
            xLabel,
            metadata,
        };
        const strategySelection = normalizeSelectionForDrilldownStrategy({
            source,
            selection,
        });

        const isTemporal = source.config?.renderOptions?.isTemporal ?? false;

        if (isTemporal && x !== undefined && x !== null) {
            if (!hasTemporalGrain(source)) {
                return false;
            }
            const dateRange = this.getDrilldownDateRange(
                x,
                source.temporalGrain,
                source,
            );

            drilldownState.startDate = dateRange.beginDate!;
            drilldownState.endDate = dateRange.endDate!;
        }

        const groupedDateRange = this.getDateGroupingDateRange(source, selection);
        if (groupedDateRange) {
            drilldownState.startDate = groupedDateRange.beginDate;
            drilldownState.endDate = groupedDateRange.endDate;
            // Both fields, because readers disagree about which one is authoritative and
            // synchronizeDateReferenceFields mirrors whichever it finds first.
            drilldownState.dateReferenceOption = groupedDateRange.dateReference;
            drilldownState.dateReference = groupedDateRange.dateReference;
        }

        const groupedCallDuration = this.getCallDurationGroupingFilters(
            source,
            selection,
        );
        if (groupedCallDuration) {
            drilldownState.minCallDuration = groupedCallDuration.minCallDuration;
            // Assigned even when undefined, so the last bucket's open-ended range clears whatever
            // upper bound the page was already carrying instead of intersecting with it.
            drilldownState.maxCallDuration = groupedCallDuration.maxCallDuration;
        }

        const groupedMessageCount = this.getMessageCountGroupingFilters(
            source,
            selection,
        );
        if (groupedMessageCount) {
            drilldownState.minMessageCount = groupedMessageCount.minMessageCount;
            drilldownState.maxMessageCount = groupedMessageCount.maxMessageCount;
        }

        const groupedModuleScores = this.getModuleScoreGroupingFilters(
            source,
            selection,
        );
        if (groupedModuleScores) {
            // Replaced rather than appended. The bucket already says everything about this module's
            // score, and a condition the page was carrying would narrow the bar rather than
            // reproduce it.
            drilldownState.moduleScores = groupedModuleScores;
        }

        drilldownState.pageNumber =
            this.selectedDataDgStore.paginationModel!.page + 1;
        drilldownState.pageSize =
            this.selectedDataDgStore.paginationModel?.pageSize;

        // handle compare prev drilldown
        if (drilldownState.previousPeriodStartDate) {
            const clickedSeriesLastDay = moment(series?.split("-")[1]).startOf(
                "date",
            );

            if (
                clickedSeriesLastDay <
                moment(drilldownState.startDate).endOf("date")
            ) {
                // clicked last day is before selected first day, so we use comparePrev dates
                drilldownState.startDate = serializeToUtc(
                    moment(drilldownState.previousPeriodStartDate),
                )!;
                drilldownState.endDate = serializeToUtc(
                    moment(drilldownState.previousPeriodEndDate),
                )!;
            }
        }

        if (x === undefined || series === undefined) {
            return true;
        }

        applyGroupingSelectionToRequestState({
            source,
            selection,
            state: drilldownState,
            applicationFilterValuesStore: this.applicationFilterValuesStore,
            optionsStore: this.optionsStore,
        });

        strategy?.applyToRequestState?.({
            selection: strategySelection,
            source,
            state: drilldownState,
            optionsStore: this.optionsStore,
            applicationFilterValuesStore: this.applicationFilterValuesStore,
            selectedTopic: this.selectedTopic,
            selectedDriver: this.selectedDriver,
        });

        return true;
    };

    private getDrilldownDataEndpoint(
        source: DrilldownSource | null = this.source,
    ): SignalDataEndpoint | undefined {
        const strategyEndpoint =
            this.strategy?.drilldownViewDataEndpoint ??
            source?.config?.drilldown?.drilldownViewDataEndpoint;
        return strategyEndpoint ?? source?.dataEndpoint;
    }

    private getTopicsDrilldownEndpoint(
        source: DrilldownSource | null = this.source,
    ): SignalDataEndpoint | undefined {
        const strategyEndpoint =
            this.strategy?.drilldownViewTopicsEndpoint ??
            source?.config?.drilldown?.drilldownViewTopicsEndpoint;
        return strategyEndpoint ?? source?.dataEndpoint;
    }

    private getDriversDrilldownEndpoint(
        source: DrilldownSource | null = this.source,
    ): SignalDataEndpoint | undefined {
        const strategyEndpoint =
            this.strategy?.drilldownViewDriversEndpoint ??
            source?.config?.drilldown?.drilldownViewDriversEndpoint;
        return strategyEndpoint ?? source?.dataEndpoint;
    }

    private getBaseFiltersForDrilldown(
        source: DrilldownSource | null = this.source,
    ): ApplicationFilters {
        const sourceMergedFilters = source?.getMergedFiltersForRequest?.();
        if (sourceMergedFilters) {
            return toJS(sourceMergedFilters);
        }

        return toJS(this.applicationFiltersStore.toFilterObject());
    }

    /**
     * Return a filters object which has filters applied based on the given parameters.
     *
     * Takes the same selection fields as {@link requestStateSetup} — including `xLabel`, which
     * despite its name carries the x-axis *identifier* (`selectDataPoint` fills it from `x.v`,
     * the Google DataTable's value cell, while `x` holds the formatted `x.f` label). Only TreeMap
     * populates a real `xIdentifier`, so grouping resolution reads `xIdentifier ?? xLabel` and both
     * setups must pass both fields or swapped-orientation grouped charts silently lose their id.
     */
    filtersSetup = ({
        source = this.source,
        x,
        series,
        seriesIdentifier,
        xIdentifier,
        xLabel,
        metadata,
    }: {
        source?: DrilldownSource | null;
        x?: string | number | null;
        series?: string | null;
        seriesIdentifier?: string;
        xIdentifier?: string;
        xLabel?: string;
        metadata?: { name: string; value: unknown }[];
    }): ApplicationFilters => {
        if (!source) {
            return this.getBaseFiltersForDrilldown(source);
        }

        const drilldownState = this.getDrilldownState();
        if (!drilldownState) {
            return this.getBaseFiltersForDrilldown(source);
        }

        const strategy = this.getStrategy(source);
        const missingCapabilities = findMissingCapabilities(strategy, source);
        if (missingCapabilities.length > 0) {
            return this.getBaseFiltersForDrilldown(source);
        }

        const newFilterObject = this.getBaseFiltersForDrilldown(source);

        const isTemporal = source.config?.renderOptions?.isTemporal ?? false;

        if (isTemporal && !!x) {
            if (!hasTemporalGrain(source)) {
                return newFilterObject;
            }
            const dateRange = this.getDrilldownDateRange(
                x,
                source.temporalGrain,
                source,
            );

            newFilterObject.startDate = dateRange.beginDate!;
            newFilterObject.endDate = dateRange.endDate!;

            if (newFilterObject.dateRangeLabel) {
                newFilterObject.dateRangeLabel = undefined;
            }
        }

        // handle compare prev drilldown
        if (drilldownState.previousPeriodStartDate) {
            const clickedSeriesLastDay = moment(series?.split("-")[1]).startOf(
                "date",
            );

            if (
                clickedSeriesLastDay <
                moment(drilldownState.startDate).endOf("date")
            ) {
                // clicked last day is before selected first day, so we use comparePrev dates
                newFilterObject.startDate = serializeToUtc(
                    moment(drilldownState.previousPeriodStartDate),
                )!;
                newFilterObject.endDate = serializeToUtc(
                    moment(drilldownState.previousPeriodEndDate),
                )!;
            }
            if (newFilterObject.dateRangeLabel) {
                newFilterObject.dateRangeLabel = undefined;
            }
        }

        const selection = {
            x,
            series,
            seriesIdentifier,
            xIdentifier,
            xLabel,
            metadata,
        };
        const strategySelection = normalizeSelectionForDrilldownStrategy({
            source,
            selection,
        });

        const groupedDateRange = this.getDateGroupingDateRange(source, selection);
        if (groupedDateRange) {
            newFilterObject.startDate = groupedDateRange.beginDate;
            newFilterObject.endDate = groupedDateRange.endDate;
            // Both fields, because readers disagree about which one is authoritative and the merged
            // filters were synchronized before this narrowing ran. The cast is for the untyped twin,
            // the same way ApplicationFiltersStore writes the pair.
            newFilterObject.dateReferenceOption = groupedDateRange.dateReference;
            (
                newFilterObject as ApplicationFilters & {
                    dateReference?: DateReferenceOption;
                }
            ).dateReference = groupedDateRange.dateReference;

            // The chart's range label ("Last 90 days") no longer describes the narrowed range, and
            // leaving it would let it win over the dates just set.
            if (newFilterObject.dateRangeLabel) {
                newFilterObject.dateRangeLabel = undefined;
            }
        }

        const groupedCallDuration = this.getCallDurationGroupingFilters(
            source,
            selection,
        );
        if (groupedCallDuration) {
            newFilterObject.minCallDuration = groupedCallDuration.minCallDuration;
            // Assigned even when undefined, so the last bucket's open-ended range clears whatever
            // upper bound the page was already carrying instead of intersecting with it.
            newFilterObject.maxCallDuration = groupedCallDuration.maxCallDuration;
        }

        const groupedMessageCount = this.getMessageCountGroupingFilters(
            source,
            selection,
        );
        if (groupedMessageCount) {
            newFilterObject.minMessageCount = groupedMessageCount.minMessageCount;
            newFilterObject.maxMessageCount = groupedMessageCount.maxMessageCount;
        }

        const groupedModuleScores = this.getModuleScoreGroupingFilters(
            source,
            selection,
        );
        if (groupedModuleScores) {
            newFilterObject.moduleScores = groupedModuleScores;
        }

        applyGroupingSelectionToFilters({
            source,
            selection,
            filters: newFilterObject,
            applicationFilterValuesStore: this.applicationFilterValuesStore,
            optionsStore: this.optionsStore,
        });

        // Replay each ZoomPathEntry through the endpoint's dedicated zoom hook
        // before the current-selection applyToFilters runs. Endpoints that
        // aren't zoom-capable just leave applyZoomEntryToFilters undefined and
        // zoomPath empty, so the loop is a no-op for them. Intentionally
        // narrow context — no `source`/`state`/`selectedTopic` — so hook
        // authors can't accidentally feed past selections current-level
        // context. See SIGNALS-DEVELOPERNOTES.md for the zoom replay rationale.
        const zoomPath = source.zoomPath ?? [];
        for (const entry of zoomPath) {
            strategy?.applyZoomEntryToFilters?.({
                entry,
                filters: newFilterObject,
                optionsStore: this.optionsStore,
                applicationFilterValuesStore:
                    this.applicationFilterValuesStore,
            });
        }

        const result =
            strategy?.applyToFilters?.({
                selection: strategySelection,
                filters: newFilterObject,
                source,
                optionsStore: this.optionsStore,
                applicationFilterValuesStore: this.applicationFilterValuesStore,
                selectedTopic: this.selectedTopic,
                selectedDriver: this.selectedDriver,
                state: drilldownState,
            }) ?? {};

        const selectedClassifierId = (result as any).selectedClassifierId;

        let currentFilterItems = _.cloneDeep(newFilterObject.rbcFilterItems ?? []);

        // Only add the classifier filter if it is not already present
        if (
            selectedClassifierId &&
            !currentFilterItems.some((currentItem) =>
                (currentItem.classifierIds ?? []).includes(
                    selectedClassifierId as string,
                ),
            )
        ) {
            // Remove empty classifiers
            currentFilterItems = currentFilterItems.filter(
                (item) => (item.classifierIds?.length ?? 0) > 0,
            );
            currentFilterItems.push({
                value: true,
                classifierIds: [selectedClassifierId],
                combinator: 0,
                filterGroupId: 0,
            });
        }

        newFilterObject.rbcFilterItems = currentFilterItems;

        // Determine what filters were updated
        // Track the updated filters within the context store
        const filtersUpdated = diffObjects(
            newFilterObject,
            this.applicationFiltersStore.toFilterObject(),
        );

        this.contextStore.setupContext({
            actionTaken: "ViewInConversations",
            filters: filtersUpdated,
            selectedSavedFilter:
                this.applicationFiltersStore.selectedSavedFilter,
        });

        return newFilterObject;
    };

    /** Columns */

    private resolveColumns(
        dataEndpoint: SignalDataEndpoint | undefined,
    ): IColDef[] {
        const normalizedEndpoint = normalizeEndpointForDrilldown(dataEndpoint);
        switch (normalizedEndpoint) {
            case SignalsEndpoints.additionalReports.ModelResultsCountId:
            case SignalsEndpoints.additionalReports.ModelResultsOverTimeId:
            case SignalsEndpoints.additionalReports.PublishedModelResultsCountId:
            case SignalsEndpoints.additionalReports
                .PublishedModelResultsOverTimeId:
            case SignalsEndpoints.additionalReports.GroupedModelResultsId:
            case SignalsEndpoints.additionalReports
                .PublishedGroupedModelResultsId:
                return this.modelResultConversationsColumns;
            case SignalsEndpoints.additionalReports.CountByQuestionResponseId:
                return this.questionResponseCountConversationsColumns;
            default:
                return this.conversationColumns;
        }
    }

    private getConversationColumnsWithoutClassification(): IColDef[] {
        return this.conversationColumns.filter((column) => {
            if (column.field !== "blobUrl") {
                column.width = undefined;
                column.flex = 1;
            }

            return column.field !== "topics" && column.field !== "driverName";
        });
    }

    private get viewDataClassification() {
        return resolveViewDataClassification({
            configuredClassification:
                this.strategy?.viewDataClassification ??
                this.source?.config?.drilldown?.viewDataClassification,
            canViewDrivers: this.authStore.canUserView("Drivers"),
        });
    }

    @computed get conversationColumns(): IColDef[] {
        let conversationColumns: IColDef[] = [
            {
                headerName: "Conversation",
                field: "audioMetadataId",
                filterable: false,
                sortable: false,
                width: 150,
                renderCell: (params: GridRenderCellParams) => {
                    const evalId = params.row.audioMetadataId;

                    return (
                        <Link
                            className="pendo-ignore"
                            style={{ color: theme.palette.gray[600] }}
                            to={Routes.makeConversationsRoute(evalId ?? "")}
                            onClick={() => {
                                // <-- Add this handler to setup for convos
                                // const isTopics =
                                //     this.selectedTopic !== null &&
                                //     this.selectedDataPoint === null;
                                // const requestStateForConvos: ApplicationFilters =
                                //     this.convosFilterRequestSetup({
                                //         chartId: isTopics
                                //             ? this.currentOnSelectTopicPayload
                                //                   .chartId
                                //             : this.currentOnSelectPayload
                                //                   .chartId,
                                //         x: isTopics
                                //             ? this.currentOnSelectTopicPayload.x
                                //             : this.currentOnSelectPayload.x,
                                //         series: isTopics
                                //             ? this.currentOnSelectTopicPayload
                                //                   .series
                                //             : this.currentOnSelectPayload
                                //                   .series,
                                //         isTopics,
                                //         seriesIdentifier:
                                //             this.currentOnSelectPayload
                                //                 .seriesIdentifier,
                                //         xIdentifier:
                                //             this.currentOnSelectPayload
                                //                 .xIdentifier,
                                //     });
                                // this.applicationFiltersStore.currentlyAppliedFilters =
                                //     _.cloneDeep(requestStateForConvos);
                            }}
                        >
                            View Conversation
                        </Link>
                    );
                },
            },
            {
                headerName: this.applicationFiltersStore.dateReferenceOption,
                field: "timestamp",
                filterable: false,
                sortable: false,
                align: "center",
                renderCell: (params) => {
                    const text = params.row.timestamp
                        ? params.row.timestamp
                        : params.row.arrivedOn;
                    return (
                        <Grid container>
                            {standardDateFormat(
                                parseFromISO(text, false),
                                "MMM DD, YYYY",
                            )}
                        </Grid>
                    );
                },
            },
            {
                headerName: "Agent Name",
                field: "agentFullName",
                type: "string",
                width: 120,
                filterable: false,
                sortable: false,
            },
            {
                headerName: "Duration",
                field: "callDurationMillis",
                width: 85,
                filterable: false,
                sortable: false,
                ...durationColumnType,
            },
            {
                headerName: "Evaluation Type",
                field: "evalType",
                renderCell: (params) => {
                    if (params.value) {
                        const StyledLink = styled(Link)(({ theme }) => ({
                            textAlign: "left",
                            color: theme.palette.gray[600],
                            textDecoration: "underline",
                            textUnderlineOffset: theme.spacing(0.5),
                            "&:hover": {
                                fontWeight: "bolder",
                            },
                        }));

                        return (
                            <StyledLink
                                to={Routes.makeEvaluationRoute(
                                    this.authStore.orgStore
                                        .selectedOrganization!.id,
                                    params.row.evaluationId,
                                )}
                                target="_blank"
                            >
                                {params.value === EvalType[1] ? "AI" : "Human"}
                            </StyledLink>
                        );
                    }
                },
                filterable: false,
                sortable: false,
                resizable: false,
                width: 100,
            },
            {
                headerName: "Evaluation Number",
                field: "evaluationQbId",
                renderCell: (params) => {
                    if (params.value && params.row.evaluationId) {
                        const StyledLink = styled(Link)(({ theme }) => ({
                            textAlign: "left",
                            color: theme.palette.gray[600],
                            textDecoration: "underline",
                            textUnderlineOffset: theme.spacing(0.5),
                            "&:hover": {
                                fontWeight: "bolder",
                            },
                        }));

                        return (
                            <StyledLink
                                to={Routes.makeEvaluationRoute(
                                    this.authStore.orgStore
                                        .selectedOrganization!.id,
                                    params.row.evaluationId,
                                )}
                                target="_blank"
                            >
                                {params.value}
                            </StyledLink>
                        );
                    }
                },
                filterable: false,
                sortable: false,
                resizable: false,
                width: 100,
            },
            this.viewDataClassification === "drivers"
                ? this.conversationDriversColumn
                : this.conversationTopicsColumn,
            {
                field: "hierarchy",
                headerName: "Service Hierarchy",
                filterable: false,
                sortable: false,
                flex: 1,
            },
        ];

        const userCannotViewMedia =
            this.authStore.canUserView("Restrict Media") &&
            !this.authStore.isUserUltra();

        const mediaPlayBackCol = {
            field: "blobUrl",
            headerName: " ",
            hideSortIcons: true,
            filterable: false,
            width: 50,
            sortable: false,
            resizable: false,
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.callDurationMillis !== 0) {
                    return new AcxFormatters().playerFormatter(params);
                } else {
                    return <></>;
                }
            },
            cellClassName: "no-padding",
        };

        if (!userCannotViewMedia) {
            conversationColumns.splice(1, 0, mediaPlayBackCol);
        }

        return conversationColumns;
    }

    private get conversationDriversColumn(): IColDef {
        return {
            headerName: "Drivers",
            field: "driverName",
            renderCell: (params) => {
                if (!params.value) return null;

                return (
                    <Grid container wrap="nowrap" alignItems="center" gap={1}>
                        <Grid item>
                            <AcxChip
                                size="small"
                                label={String(params.value)}
                                color="gray"
                            />
                        </Grid>
                    </Grid>
                );
            },
            filterable: false,
            sortable: false,
            flex: 1,
        };
    }

    private get conversationTopicsColumn(): IColDef {
        return {
            headerName: "Topics",
            field: "topics",
            renderCell: (params) => {
                const topicData: { label: string; description: string }[] = [];
                for (const key in params.value as any) {
                    if (
                        params.value &&
                        (key === "topic1" ||
                            key === "topic2" ||
                            key === "topic3")
                    ) {
                        topicData.push({
                            label: params.value[key],
                            description: params.value[key + "Reasoning"],
                        });
                    }
                }

                return (
                    <Grid
                        container
                        wrap="nowrap"
                        alignItems={"center"}
                        gap={1}
                    >
                        {topicData.map((data, index) => {
                            if (data.label && data.description) {
                                return (
                                    <Grid item key={index}>
                                        <AcxChip
                                            size="small"
                                            label={data.label}
                                            color="gray"
                                            customTooltip={data.description}
                                        />
                                    </Grid>
                                );
                            }
                            return null;
                        })}
                    </Grid>
                );
            },
            filterable: false,
            sortable: false,
            flex: 1,
        };
    }

    @computed get modelResultConversationsColumns(): IColDef[] {
        return [
            ...this.conversationColumns,
            {
                field: "modelReasoning",
                headerName: "Model Reasoning",
                filterable: false,
                sortable: false,
                flex: 0.5,
                renderCell(params) {
                    if (!params.row.modelReasoning) return null;

                    return (
                        <Tooltip
                            PopperProps={{
                                popperOptions: {
                                    placement: "bottom-start",
                                },
                            }}
                            title={
                                <Markdown>{params.row.modelReasoning}</Markdown>
                            }
                        >
                            {params.row.modelReasoning}
                        </Tooltip>
                    );
                },
            },
        ];
    }

    @computed get questionResponseCountConversationsColumns(): IColDef[] {
        return [
            ...this.conversationColumns,
            {
                field: "tag",
                headerName: "Tag",
                filterable: false,
                sortable: false,
                flex: 0.2,
            },
            {
                field: "notes",
                headerName: "Notes",
                filterable: false,
                sortable: false,
                flex: 0.5,
            },
            {
                field: "lastModified",
                headerName: "Last Modified",
                filterable: false,
                sortable: false,
                flex: 0.5,
                ...dateColumnType,
            },
            {
                field: "lastModifiedBy",
                headerName: "Last Modified By",
                filterable: false,
                sortable: false,
                flex: 0.5,
            },
        ];
    }

    @computed get topicsColumns(): IColDef[] {
        return [
            {
                headerName: "Topics",
                field: "name",
                type: "string",
                flex: 6,
                filterable: false,
                cellClassName: "clickable-cell",
                renderCell: (params: GridRenderCellParams) => {
                    return <AcxClickableCell>{params.value}</AcxClickableCell>;
                },
            },
            {
                headerName: "Percent of Theme",
                field: "percent",
                renderCell: (params) => {
                    return (
                        <Grid
                            container
                            item
                            xs={12}
                            wrap="nowrap"
                            justifyContent={"center"}
                            alignItems={"center"}
                            gap={1}
                        >
                            <Grid item xs={10}>
                                <AcxProgress
                                    key={params.id}
                                    percentComplete={params.value?.toString()}
                                    backgroundColor={theme.palette.primary.main}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                {params.value}%
                            </Grid>
                        </Grid>
                    );
                },
                filterable: false,
                flex: 2,
            },

            {
                headerName: "Volume",
                field: "volume",
                filterable: false,
                flex: 1,
            },
        ];
    }

    /** Driver counterpart of {@link topicsColumns}, used by the View Drivers aggregation. */
    @computed get driversColumns(): IColDef[] {
        return [
            {
                headerName: "Drivers",
                field: "name",
                type: "string",
                flex: 6,
                filterable: false,
                cellClassName: "clickable-cell",
                renderCell: (params: GridRenderCellParams) => {
                    return <AcxClickableCell>{params.value}</AcxClickableCell>;
                },
            },
            {
                headerName: "Percent of Theme",
                field: "percent",
                renderCell: (params) => {
                    return (
                        <Grid
                            container
                            item
                            xs={12}
                            wrap="nowrap"
                            justifyContent={"center"}
                            alignItems={"center"}
                            gap={1}
                        >
                            <Grid item xs={10}>
                                <AcxProgress
                                    key={params.id}
                                    percentComplete={params.value?.toString()}
                                    backgroundColor={theme.palette.primary.main}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                {params.value}%
                            </Grid>
                        </Grid>
                    );
                },
                filterable: false,
                flex: 2,
            },

            {
                headerName: "Volume",
                field: "volume",
                filterable: false,
                flex: 1,
            },
        ];
    }
}

export default DrilldownStore;
